import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Define our data structure
interface ThankYouGroup {
  title: string;
  names: string[];
}

export function ThanksPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [scrollSpeed, setScrollSpeed] = useState(1); // 1 = normal speed
  
  // Data for the thank you page
  const groups: ThankYouGroup[] = [
    {
      title: "Famille",
      names: [
        "AEM Family",
        "Samuel st Jean",
        "Irma Lucien",
        "Jovanica Laventure",
        "iressaint St-Vil",
        "Shenglee Desrosiers",
        "Donald Alexandre",
        "Sarah Severe",
        "kerene Lormil",
        "Wensly Alexandre",
        "David Antoine",
        "Eliane Lucien",
        "Eric Lucien",
        "Yanick Menard Roche",
        "Marie Denise Fleurancois",
        "Pasteur Frantz",
        "Emmanuella Dorvil"
      ]
    },
    {
      title: "Amis",
      names: [
        "Mardocheé amille",
        "Nahama Dimanche",
        "Rebecca Paul",
        "Edwige St Juste et son Mari",
        "Paul Zacharie Oscar",
        "Amos Paul et sa Famille",
        "Edward Paul",
        "Jude Mera et sa femme",
        "Evatson St Victor",
        "Archille Peralte",
        "Delucson Lubin",
        "Juvens Delva",
        "Thelemaque Getro",
        "dieudonné Daniel",
        "Woodlaire Belizaire",
        "Bien-aime Ronaldo",
        "Sheldon",
        "Pericles Herve",
        "Thamare Monthoute",
        "Wendy Lucien",
        "Phianley JR",
        "Caceus Mayva",
        "Christy St Fleur",
        "John Woody Foreste",
        "Aland Jean",
        "Dayana Jean",
        "Bonflo Haiti",
        "Johanne Augustin",
        "Saraldjine Lavenal",
        "Marie Carmelle Etienne Et sa Famille",
        "Immacula Etienne",
        "Solange St vil",
        "Carline St Vil",
        "Eveline Mérisier",
        "Rebecca Paul",
        "Dieune Pierre",
        "Saintilmé Wilkensonn",
        "Jean Widson Foreste",
        "Payoute Nesly",
        "Techmway Alexis",
        "Rosemonde Pierre",
        "Christopher Charitus",
        "Ronald Sylvain",
        "Jerry Arida",
        "Wadson Arisda",
        "Picard",
        "Phanor Lens",
        "Danise Michel",
        "Justin Pierre et son mari",
        "Lendony Germain",
        "Eden Pierre",
        "Yousemitha Hypolite",
        "Phidona Altiodre",
        "Youdeline Bigot",
        "Somaya Bernard",
        "Rojanda Jean",
        "Laica Dutrevil",
        "Nicolas James",
        "Mitouse Joseph",
        "Woodley Bossuet (Dj Wood)",
        "T-Tig",
        "Caven",
        "PDG RAP",
        "Raymonde Pierre",
        "Clara (marenn mw fanm st Marc loll)",
        "Dapepe",
        "Dorcilien Rosekerchie",
        "Genesis",
        "Ivete",
        "Cochy Jolette",
        "Junior Egalité",
        "Kendiana Geffrard",
        "Hugo (le Voyageur)",
        "Liline Bourjolie",
        "Lorvens (AEm Family)",
        "Loudine Desty",
        "Maestro Willy",
        "Master Bradley",
        "Nadedy et son Mari",
        "Odniel",
        "Ronise",
        "Severe James",
        "Sonel Joseph",
        "Sr Myrlande",
        "Widreille Samy Seriphin",
        "Benito (Ti fokè)",
        "Vilbru Anirose",
        "Wanachil",
        "Wespy",
        "Kerry",
        "Steevenson (Steevy boy *fort National*)",
        "Roody Predvil",
        "Melchior Predvil",
        "Jerry Arisda",
        "Alixon Tine",
        "Angelot",
        "Ronald ANtoine",
        "Ynose",
        "Zurvan",
        "Buske",
        "Bernel Benjamin",
        "Germain Lendony",
        "Eugene Peleg",
        "Lainé Guyvensky",
        "Shelo Moreth",
        "Katka",
        "Malia (Jerusalem 8)",
        "Mackenlove ( Jerusalem 8)",
        "Riguene Atisme",
        "Roody Gustin",
        "Beltifi.com",
        "Oralus",
        "Emanise Dorvil",
        "Adoratrice Sylrose",
        "Actrice Sandra",
        "Jean Obed",
        "Justance Maxence",
        "Guerson Mondesir",
        "Elysée et Wilsy",
        "Yveline Jeanty",
        "Lavita",
        "Wisnarder Palme",
        "Saint-Vil Vanel",
        "Lena Cajuste",
        "Ypsley Vilbon",
        "Team Ajanten nan Cebel",
        "Moun Jerusalem yo",
        "Ti mesye sou fort National yo"
      ]
    },
    {
      title: "Ecole",
      names: [
        "Mission de l'espoir",
        "CEBEl",
        "Sapiens"
      ]
    },
    {
      title: "Ecole Professionnel",
      names: [
        "Haiti Tec"
      ]
    }
  ];

  // Auto-scrolling effect
  useEffect(() => {
    if (!autoScroll || !scrollRef.current) return;
    
    const scrollContainer = scrollRef.current;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;
    const maxScrollTop = scrollHeight - clientHeight;
    
    let startTime: number | null = null;
    const scrollDuration = 120000 / scrollSpeed; // 2 minutes to scroll through all content at normal speed
    
    const animateScroll = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      // Calculate how far to scroll
      const scrollProgress = Math.min(elapsed / scrollDuration, 1);
      const newScrollTop = scrollProgress * maxScrollTop;
      
      // Apply scroll
      scrollContainer.scrollTop = newScrollTop;
      
      // If we haven't reached the end, continue animation
      if (scrollProgress < 1 && autoScroll) {
        requestAnimationFrame(animateScroll);
      } else if (scrollProgress >= 1) {
        // When we reach the end, restart from the top after a delay
        setTimeout(() => {
          if (scrollRef.current && autoScroll) {
            scrollRef.current.scrollTop = 0;
            startTime = null;
            requestAnimationFrame(animateScroll);
          }
        }, 5000); // 5 second pause at the end
      }
    };
    
    const animationId = requestAnimationFrame(animateScroll);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [autoScroll, scrollSpeed]);

  // Handle manual scroll to pause auto-scroll
  const handleManualScroll = () => {
    setAutoScroll(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary to-blue-800 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <Link to="/">
            <Button variant="outline" className="text-white border-white hover:bg-white/10">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour à l'accueil
            </Button>
          </Link>
          
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setAutoScroll(!autoScroll)}
              className={`px-4 py-2 rounded-md ${autoScroll ? 'bg-white/20' : 'bg-white/10'} hover:bg-white/30 transition-colors`}
            >
              {autoScroll ? 'Pause' : 'Auto-scroll'}
            </button>
            
            {autoScroll && (
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setScrollSpeed(Math.max(0.5, scrollSpeed - 0.5))}
                  className="p-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
                  disabled={scrollSpeed <= 0.5}
                >
                  -
                </button>
                <span>Vitesse: {scrollSpeed}x</span>
                <button 
                  onClick={() => setScrollSpeed(Math.min(3, scrollSpeed + 0.5))}
                  className="p-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
                  disabled={scrollSpeed >= 3}
                >
                  +
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Title */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">
            Remerciements
          </h1>
          <p className="text-xl text-white/80">
            Nous tenons à remercier chaleureusement toutes les personnes qui ont contribué à notre succès
          </p>
          <div className="mt-6 flex justify-center">
            <Heart className="h-12 w-12 text-red-400 animate-pulse" />
          </div>
        </div>
        
        {/* Scrolling content */}
        <div 
          ref={scrollRef}
          className="bg-white/10 backdrop-blur-sm rounded-xl p-8 max-h-[60vh] overflow-y-auto"
          onWheel={handleManualScroll}
          onTouchMove={handleManualScroll}
        >
          {groups.map((group, groupIndex) => (
            <div key={groupIndex} className="mb-12 last:mb-0">
              <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-white/20">
                {group.title}
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.names.map((name, nameIndex) => (
                  <li 
                    key={nameIndex}
                    className="p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Footer */}
        <div className="mt-12 text-center text-white/80">
          <p>Merci à tous pour votre soutien et votre confiance.</p>
          <p className="mt-2">AEM Marketplace - 2025</p>
        </div>
      </div>
    </div>
  );
}